import React, { useState, useRef, useEffect } from 'react';
import { Shield, Send, Bell, Users, AlertCircle, Check, Loader2, History, MessageSquare, CheckCircle, FileText, Trash2, X } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import ConfirmDialog from '../components/ConfirmDialog';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = supabaseUrl ? createClient(supabaseUrl, supabaseKey) : null;

const ADMIN_USER_ID = 'admin_001';
const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

// Format date as "Mon, 2 Feb 2026, 10:30 AM"
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('en-GB', { 
    weekday: 'short', 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return `${dateStr}, ${timeStr}`;
};

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState<string[]>(Array(6).fill(''));
  const [pinError, setPinError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [notificationLogs, setNotificationLogs] = useState<any[]>([]);
  const [userMessages, setUserMessages] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [title, setTitle] = useState('Hello from PWA Demo!');
  const [message, setMessage] = useState('This is a push notification sent from the admin panel.');
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'send' | 'logs' | 'messages' | 'audit'>('send');
  const [isAdminSubscribed, setIsAdminSubscribed] = useState(false);
  const [isEnablingNotifications, setIsEnablingNotifications] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    messageId?: string;
  }>({ isOpen: false });

  useEffect(() => {
    if (isAuthenticated && supabase) {
      loadSubscriptions();
      loadNotificationLogs();
      loadUserMessages();
      loadAuditLogs();
      checkAdminSubscription();
    }
  }, [isAuthenticated]);

  const loadSubscriptions = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('push_subscriptions')
      .select('*, demo_users(device_label)')
      .eq('is_enabled', true)
      .order('created_at', { ascending: false });
    
    setSubscriptions(data || []);
  };

  const loadNotificationLogs = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('notification_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    
    setNotificationLogs(data || []);
  };

  const loadUserMessages = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('notification_logs')
      .select('*')
      .eq('type', 'user_to_admin')
      .order('created_at', { ascending: false })
      .limit(20);
    
    setUserMessages(data || []);
  };

  const loadAuditLogs = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('admin_actions')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    
    setAuditLogs(data || []);
  };

  const handleDeleteClick = (messageId: string) => {
    setConfirmDialog({ isOpen: true, messageId });
  };

  const handleConfirmDelete = async () => {
    if (!supabase || !confirmDialog.messageId) return;

    setDeletingMessageId(confirmDialog.messageId);

    try {
      const { error } = await supabase
        .from('notification_logs')
        .delete()
        .eq('id', confirmDialog.messageId);

      if (error) throw error;

      // Remove from local state
      setUserMessages(prev => prev.filter(msg => msg.id !== confirmDialog.messageId));
      setSendResult({ type: 'success', text: 'Message deleted successfully' });
      setTimeout(() => setSendResult(null), 3000);
      
      // Close dialog after successful deletion
      setConfirmDialog({ isOpen: false });
    } catch (error) {
      console.error('Error deleting message:', error);
      setSendResult({ type: 'error', text: 'Failed to delete message' });
      setConfirmDialog({ isOpen: false });
    } finally {
      setDeletingMessageId(null);
    }
  };

  const checkAdminSubscription = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('push_subscriptions')
      .select('*')
      .eq('user_id', ADMIN_USER_ID)
      .eq('is_enabled', true)
      .single();
    
    setIsAdminSubscribed(!!data);
  };

  const enableAdminNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('Push notifications are not supported in this browser');
      return;
    }

    setIsEnablingNotifications(true);

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        alert('Notification permission denied');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });

      await supabase?.from('demo_users').upsert(
        { user_id: ADMIN_USER_ID, device_label: 'Admin Device', user_agent: navigator.userAgent },
        { onConflict: 'user_id' }
      );

      await supabase?.from('push_subscriptions').insert({
        user_id: ADMIN_USER_ID,
        subscription: subscription.toJSON(),
        device_label: 'Admin Device',
        is_enabled: true,
      });

      setIsAdminSubscribed(true);
      alert('Admin notifications enabled! You will now receive messages from users.');
    } catch (error) {
      console.error('Error enabling admin notifications:', error);
      alert('Failed to enable notifications: ' + error.message);
    } finally {
      setIsEnablingNotifications(false);
    }
  };

  const handlePinChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setPinError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newPin.every((digit, i) => i === index ? digit !== '' : digit !== '') && index === 5) {
      handleVerifyPin(newPin.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyPin = async (pinValue: string) => {
    if (pinValue.length !== 6) {
      setPinError('Please enter a 6-digit PIN');
      return;
    }

    if (!supabase) {
      setPinError('Supabase not configured');
      return;
    }

    setIsVerifying(true);
    setPinError('');

    try {
      const { data, error} = await supabase.functions.invoke('verify-admin-demo', {
        body: { pin: pinValue }
      });

      if (error) throw error;

      if (data?.verified) {
        setIsAuthenticated(true);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= 3) {
          setPinError('Too many failed attempts. Please try again later.');
        } else {
          setPinError(`Incorrect PIN. ${3 - newAttempts} attempt(s) remaining.`);
          setPin(Array(6).fill(''));
          inputRefs.current[0]?.focus();
        }
      }
    } catch (error) {
      console.error('Error verifying PIN:', error);
      setPinError('Error verifying PIN. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSendNotification = async () => {
    if (!supabase) {
      setSendResult({ type: 'error', text: 'Supabase not configured' });
      return;
    }

    setIsSending(true);
    setSendResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('send-notifications-demo', {
        body: {
          title: title,
          message: message,
          type: 'admin_broadcast',
          triggeredBy: 'admin',
          triggeredFromDevice: 'PWA Demo Admin Panel'
        }
      });

      if (error) throw error;

      const summary = data?.summary || { sent: 0, failed: 0, total: 0 };
      setSendResult({ 
        type: 'success', 
        text: `Sent to ${summary.sent}/${summary.total} device(s)${summary.failed > 0 ? ` (${summary.failed} failed)` : ''}` 
      });
      
      setTimeout(() => setSendResult(null), 5000);
    } catch (error) {
      console.error('Error sending notification:', error);
      setSendResult({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setIsSending(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen lg:min-h-[80vh] flex items-center justify-center px-4 py-8 overflow-x-hidden">
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-10 w-full max-w-md border-2 border-gray-100">
          <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-50 rounded-2xl flex items-center justify-center">
              <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-gray-800 tracking-tight">Admin Portal</h2>
              <p className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-widest">Authorized Access Only</p>
            </div>
          </div>

          <div className="mb-6 sm:mb-8 relative">
            {isVerifying && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-2xl">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest">Verifying...</span>
                </div>
              </div>
            )}
            <div className="flex gap-1.5 sm:gap-2 justify-center">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-10 h-14 sm:w-12 sm:h-16 text-center text-xl sm:text-2xl font-black rounded-xl border-2 border-gray-50 focus:border-purple-500 focus:bg-white transition-all outline-none bg-gray-50 text-gray-900"
                  disabled={attempts >= 3 || isVerifying}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          {pinError && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-100 rounded-2xl flex items-center gap-3 animate-shake">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <p className="text-xs font-bold text-red-700">{pinError}</p>
            </div>
          )}

          <div className="text-center text-xs sm:text-sm text-gray-500">
            <p className="mb-2">Enter the 6-digit admin PIN</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-4 sm:py-8 pb-safe overflow-x-hidden">
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-gray-900 mb-2 break-words">Admin Panel</h1>
        <p className="text-base sm:text-lg text-gray-600 break-words">Manage notifications & messages</p>
      </div>

      {sendResult && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
          sendResult.type === 'success' ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'
        }`}>
          {sendResult.type === 'success' ? (
            <Check className="w-6 h-6 text-green-600" />
          ) : (
            <AlertCircle className="w-6 h-6 text-red-600" />
          )}
          <p className={sendResult.type === 'success' ? 'text-green-800 font-semibold' : 'text-red-800 font-semibold'}>
            {sendResult.text}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 sm:gap-2 mb-6 border-b-2 border-gray-200 overflow-x-auto hide-scrollbar -mx-4 px-4">
        <button
          onClick={() => setActiveTab('send')}
          className={`px-3 sm:px-6 py-2.5 sm:py-3 font-bold transition-all whitespace-nowrap tap-target text-xs sm:text-base ${
            activeTab === 'send'
              ? 'text-orange-600 border-b-2 border-orange-600 -mb-[2px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Send className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Send Notification</span>
          <span className="sm:hidden">Send</span>
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-3 sm:px-6 py-2.5 sm:py-3 font-bold transition-all whitespace-nowrap tap-target text-xs sm:text-base ${
            activeTab === 'logs'
              ? 'text-purple-600 border-b-2 border-purple-600 -mb-[2px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <History className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Notification Logs</span>
          <span className="sm:hidden">Logs</span>
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-3 sm:px-6 py-2.5 sm:py-3 font-bold transition-all whitespace-nowrap relative tap-target text-xs sm:text-base ${
            activeTab === 'messages'
              ? 'text-blue-600 border-b-2 border-blue-600 -mb-[2px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
          <span className="hidden sm:inline">User Messages</span>
          <span className="sm:hidden">Messages</span>
          {userMessages.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {userMessages.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-3 sm:px-6 py-2.5 sm:py-3 font-bold transition-all whitespace-nowrap tap-target text-xs sm:text-base ${
            activeTab === 'audit'
              ? 'text-green-600 border-b-2 border-green-600 -mb-[2px]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Audit Log</span>
          <span className="sm:hidden">Audit</span>
        </button>
      </div>

      {/* Admin Notification Subscription Banner (below tabs) */}
      {!isAdminSubscribed && (
        <div className="mb-6 p-4 rounded-xl bg-blue-50 border-2 border-blue-200 flex items-start gap-3">
          <Bell className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-blue-900 font-bold mb-2">Enable Admin Notifications</p>
            <p className="text-sm text-blue-700 mb-3">Get notified when users send you messages</p>
            <button
              onClick={enableAdminNotifications}
              disabled={isEnablingNotifications}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 tap-target touch-feedback"
            >
              {isEnablingNotifications ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  Enable Now
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'send' && (
        <>
          {/* Active Devices */}
          <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-gray-700" />
              <h2 className="text-xl font-black text-gray-900">Active Devices</h2>
            </div>
            {subscriptions.length > 0 ? (
              <div className="space-y-2">
                {subscriptions.map((sub) => (
                  <div key={sub.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Bell className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                  <p className="font-semibold text-gray-900">{sub.device_label || 'Unknown Device'}</p>
                  <p className="text-xs text-gray-500">
                    User: {sub.user_id?.substring(0, 12)}... | 
                    Registered: {formatDate(sub.created_at)}
                  </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No devices registered yet. Enable notifications from the Notifications page first.
              </p>
            )}
          </div>
        </>
      )}

      {activeTab === 'logs' && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <History className="w-6 h-6 text-purple-700" />
            <h2 className="text-xl font-black text-gray-900">Notification History</h2>
            <button
              onClick={loadNotificationLogs}
              className="ml-auto text-sm text-purple-600 hover:text-purple-700 font-semibold"
            >
              Refresh
            </button>
          </div>
          {notificationLogs.length > 0 ? (
            <div className="space-y-2 max-h-[600px] overflow-y-auto hide-scrollbar">
              {notificationLogs.map((log) => (
                <div key={log.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      log.status === 'sent' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {log.status === 'sent' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900">{log.title}</p>
                      <p className="text-sm text-gray-600 break-words">{log.message}</p>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                        <span className="bg-gray-200 px-2 py-1 rounded">
                          {log.status === 'sent' ? '✓ Sent' : '✗ Failed'}
                        </span>
                        <span className="bg-gray-200 px-2 py-1 rounded">
                          {log.type || 'broadcast'}
                        </span>
                        {log.device_label && (
                          <span className="bg-gray-200 px-2 py-1 rounded">
                            {log.device_label}
                          </span>
                        )}
                        <span className="bg-gray-200 px-2 py-1 rounded">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                      {log.error && (
                        <p className="text-xs text-red-600 mt-1">Error: {log.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No notification logs yet. Send a notification first!
            </p>
          )}
        </div>
      )}

      {activeTab === 'messages' && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className="w-6 h-6 text-blue-700" />
            <h2 className="text-xl font-black text-gray-900">Messages from Users</h2>
            <button
              onClick={loadUserMessages}
              className="ml-auto text-sm text-blue-600 hover:text-blue-700 font-semibold tap-target"
            >
              Refresh
            </button>
          </div>
          {userMessages.length > 0 ? (
            <div className="space-y-3 max-h-[600px] overflow-y-auto hide-scrollbar">
              {userMessages.map((msg) => (
                <div key={msg.id} className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 relative group">
                  <button
                    onClick={() => handleDeleteClick(msg.id)}
                    disabled={deletingMessageId === msg.id}
                    className="absolute top-2 right-2 p-2 bg-white hover:bg-red-50 text-red-600 rounded-lg border border-red-200 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50 tap-target"
                    title="Delete message"
                  >
                    {deletingMessageId === msg.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                  <div className="flex items-start gap-3 pr-12">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900">{msg.title}</p>
                      <p className="text-sm text-gray-700 mt-1 break-words">{msg.message}</p>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-600">
                        {msg.user_id && (
                          <span className="bg-white px-2 py-1 rounded border border-blue-200">
                            From: {msg.user_id.substring(0, 12)}...
                          </span>
                        )}
                        {msg.device_label && (
                          <span className="bg-white px-2 py-1 rounded border border-blue-200">
                            Device: {msg.device_label}
                          </span>
                        )}
                        <span className="bg-white px-2 py-1 rounded border border-blue-200">
                          {formatDate(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No user messages yet. Users can send you messages from the Notifications page!
            </p>
          )}
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-green-700" />
            <h2 className="text-xl font-black text-gray-900">Admin Action Audit Log</h2>
            <button
              onClick={loadAuditLogs}
              className="ml-auto text-sm text-green-600 hover:text-green-700 font-semibold tap-target"
            >
              Refresh
            </button>
          </div>
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs text-green-800">
              <strong>🔒 Security CCTV:</strong> All admin actions are logged here for security monitoring and compliance. This includes login attempts, notifications sent, and configuration changes.
            </p>
          </div>
          {auditLogs.length > 0 ? (
            <div className="space-y-2 max-h-[600px] overflow-y-auto hide-scrollbar">
              {auditLogs.map((log) => (
                <div key={log.id} className={`p-4 rounded-lg border-2 ${
                  log.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      log.success ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {log.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <X className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-bold text-gray-900">{log.action_type.replace(/_/g, ' ').toUpperCase()}</p>
                        <span className={`text-xs font-bold px-2 py-1 rounded ${
                          log.success 
                            ? 'bg-green-200 text-green-800' 
                            : 'bg-red-200 text-red-800'
                        }`}>
                          {log.success ? 'SUCCESS' : 'FAILED'}
                        </span>
                      </div>
                      {log.action_details && (
                        <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
                            {JSON.stringify(log.action_details, null, 2)}
                          </pre>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-600">
                        {log.admin_identifier && (
                          <span className="bg-white px-2 py-1 rounded border border-gray-300">
                            Admin: {log.admin_identifier}
                          </span>
                        )}
                        {log.ip_address && (
                          <span className="bg-white px-2 py-1 rounded border border-gray-300">
                            IP: {log.ip_address}
                          </span>
                        )}
                        <span className="bg-white px-2 py-1 rounded border border-gray-300">
                          {formatDate(log.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No audit logs yet. Admin actions will appear here as they occur.
            </p>
          )}
        </div>
      )}

      {activeTab === 'send' && (
        <>
          {/* Send Notification */}
          <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-4 sm:p-8 text-white">
            <h2 className="text-xl sm:text-2xl font-black mb-4 sm:mb-6">Send Push Notification</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-bold mb-2 text-white/90">Notification Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full p-3 rounded-xl text-gray-900 font-semibold"
                  placeholder="Enter title"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-white/90">Notification Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full p-3 rounded-xl text-gray-900 h-24 resize-none"
                  placeholder="Enter message"
                />
              </div>
            </div>

            <button
              onClick={handleSendNotification}
              disabled={isSending || subscriptions.length === 0}
              className="w-full bg-white text-orange-600 px-8 py-4 rounded-xl font-bold hover:bg-orange-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-feedback tap-target"
            >
              {isSending ? (
                <>
                  <div className="w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send to All Devices ({subscriptions.length})
                </>
              )}
            </button>

            {subscriptions.length === 0 && (
              <p className="text-center text-white/70 text-sm mt-3">
                Enable notifications on the Notifications page first
              </p>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 mt-6">
            <h3 className="font-bold text-lg text-gray-900 mb-4">📖 How to Use</h3>
            <ol className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-bold text-orange-500">1.</span>
                <span>Go to the Notifications page and enable notifications</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-orange-500">2.</span>
                <span>Return here to see the device listed above</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-orange-500">3.</span>
                <span>Customize the notification title and message</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-orange-500">4.</span>
                <span>Click "Send" - notification will be pushed even if app is closed!</span>
              </li>
            </ol>
          </div>
        </>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={handleConfirmDelete}
        title="Delete Message?"
        message="Are you sure you want to delete this message? This action cannot be undone and the message will be permanently removed from the system."
        confirmText="Delete Message"
        cancelText="Keep Message"
        variant="danger"
        isLoading={!!deletingMessageId}
      />
    </div>
  );
}
